import {paramsToURL} from '@js/modules/paramsToUrl';
import I18n from '@js/modules/translations';

import Notification from '@js/components/layouts/notification';

import {pushError} from '@js/actions/errorManagement';
import {handleJsonResponse} from '@js/actions/jsonFormatter';


const _getHeaders = (external = false) => {
    if (external) {
        return {
            headers: new Headers({
                Accept: 'application/json',
                // 'Content-Type': 'application/json'
            })
        };
    } else {
        const csrfToken = document.getElementsByName('csrf-token')[0];
        const token = csrfToken?.getAttribute('content');

        return {
            credentials: 'same-origin',
            headers: new Headers({
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-Token': token
            })
        };
    }
};

const _getDataHeaders = () => {
    const csrfToken = document.getElementsByName('csrf-token')[0];
    const token = csrfToken?.getAttribute('content');

    return {
        credentials: 'same-origin',
        headers: new Headers({
            'X-CSRF-Token': token,
            Accept: 'application/json'
        })
    };
};

const _processSerializeParams = (value, formData, parent) => {
    const processedKey = parent || '';
    const excludeNull = true;
    const useBrackets = true;
    const arrayIndexes = true;
    const useDotSeparator = false;

    if (value === null || value === undefined) {
        if (!excludeNull) {
            formData.append(processedKey, '');
        }
        return;
    }

    if (value instanceof File) {
        formData.append(processedKey, value);
        return;
    }

    if (value instanceof Blob) {
        formData.append(processedKey, value);
        return;
    }

    if (Array.isArray(value)) {
        value.forEach((item, index) => {
            let computedKey = processedKey;
            if (useBrackets) {
                computedKey += `[${arrayIndexes ? index : ''}]`;
            }
            _processSerializeParams(item, formData, computedKey);
        });
        return;
    }

    if (typeof value === 'object') {
        Object.entries(value)
            .forEach(([key, data]) => {
                let computedKey = key;
                if (parent) {
                    computedKey = useDotSeparator
                        ? `${parent}.${key}`
                        : `${parent}[${key}]`;
                }
                _processSerializeParams(data, formData, computedKey);
            });
        return;
    }

    if (typeof value === 'boolean') {
        formData.append(processedKey, value ? 'true' : 'false');
        return;
    }

    formData.append(processedKey, value);
};

const _serializeParams = (params) => {
    const formData = new FormData();

    _processSerializeParams(params, formData);

    return formData;
};

const _reportError = (error, errorInfo) => {
    if (!error.bodyUsed) {
        const contentType = error.headers.get('content-type');
        if (contentType && contentType.indexOf('application/json') !== -1) {
            return error.json()
                .then((parsedError) => {
                    if (process.env.NODE_ENV === 'development') {
                        window.log_on_screen([parsedError.errors, parsedError.details, parsedError.message].filter(Boolean)
                            .join(' / ')
                            .split('\n')
                            .slice(0, 10));
                    } else {
                        Notification.error(I18n.t('js.helpers.errors.server'));
                    }

                    pushError(error, {...errorInfo, ...parsedError});
                });
        } else {
            return error.text()
                .then((text) => {
                    if (process.env.NODE_ENV === 'development') {
                        window.log_on_screen(
                            text.split('\n')
                                .slice(0, 10)
                        );
                    } else {
                        Notification.error(I18n.t('js.helpers.errors.server'));
                    }

                    pushError(error, {...errorInfo});
                });
        }
    } else {
        if (process.env.NODE_ENV === 'development') {
            window.log_on_screen(
                error.split('\n')
                    .slice(0, 10)
            );
        } else {
            Notification.error(I18n.t('js.helpers.errors.server'));
        }

        pushError(error, errorInfo);
    }
};

const _manageError = (origin, error, url, external = false) => {
    if (url === '/errors') {
        return;
    }

    const errorInfo = {
        origin,
        url
    };

    if (error.statusText === 'Canceled' || error.statusText === 'Cancelled') {
        return error;
    } else if (external) {
        if (error.status === 400) {
            return error;
        }

        _reportError(error, errorInfo);
    } else if (error.status === 403 && !external) {
        // Forbidden

        // Notification.error(I18n.t('js.helpers.errors.not_authorized'));
        // if (document.referrer === '') {
        //     window.location = '/';
        // } else {
        //     history.back();
        // }

        return error;
    } else if (error.status === 404 && !external) {
        // Not Found

        // Notification.error(I18n.t('js.helpers.errors.unprocessable'));
        // } else if (error.statusText === 'Unprocessable Entity') {
        // Managed by _handleResponse
        // if (!error.bodyUsed) {
        //     return error.json().then((status) => (
        //         Notification.error(status.error || status.errors || error.statusText)
        //     ));
        // } else {
        //     Notification.error(I18n.t('js.helpers.errors.unprocessable'));
        // }

        return error;
    } else if (error.status === 422) {
        // Unprocessable content: error returned when a limitation is reached or object cannot be created or updated

        return error;
    } else if (error.status === 500) {
        // Internal Server Error

        _reportError(error, errorInfo);
    } else {
        pushError(error, errorInfo);
    }
};

// A 405 whose body is consumed here can no longer be read downstream: hand the parsed body back
// in a response-shaped object so the normal error path still sees the same status.
const _asReadResponse = (response, body) => ({
    status: response.status,
    ok: response.ok,
    bodyUsed: false,
    // Browsers drop the reason phrase over HTTP/2, so statusText is ''. Falling back to the status
    // code keeps `errors` truthy: a falsy one is read as SUCCESS by the callers (Signup.svelte, and
    // middlewares/mutation.js, which would dispatch "saved" on a request that saved nothing).
    statusText: response.statusText || String(response.status),
    // pushError branches on `url` first: without it a genuine 405 reaches Sentry as an unusable
    // object message with no URL and no extras.
    url: response.url,
    headers: response.headers,
    json: () => Promise.resolve(body || {}),
    // _handleResponse picks its reader from the content-type, which is copied verbatim: a 405 coming
    // from nginx or a WAF is text/html, and without text() the real error would be replaced by
    // "response.text is not a function".
    text: () => Promise.resolve(typeof body === 'string' ? body : (response.statusText || String(response.status)))
});

// An expired CSRF token (a page left open, or served from the PWA cache) makes Rails raise
// InvalidAuthenticityToken; application_controller#not_connected_error answers 405 WITH a freshly
// minted token, precisely so the request can be replayed. Honour that contract: adopt the token and
// resend once, and never reload the page — that would throw away the form (or the drawn route) the
// user is in the middle of.
const _handleTokenError = (response, retry) => {
    if (!retry || !response || response.status !== 405 || response.bodyUsed) {
        return response;
    }

    // Scoped to the parse alone: a rejected REPLAY must keep propagating to _handleParseErrors (which
    // turns a network failure into {errors: 'offline'}). Swallowing it would hand the caller a
    // body-consumed response that reads back as {errors: undefined} — which Signup.svelte takes as
    // success, telling the visitor they have an account that was never created.
    return response.json()
        .catch(() => null)
        .then((status) => {
            if (!status || !status.token) {
                return _asReadResponse(response, status);
            }

            const csrfToken = document.getElementsByName('csrf-token')[0];
            csrfToken?.setAttribute('content', status.token);

            // Deliberately NOT re-wrapped in _handleTokenError: that is the whole one-shot guarantee.
            return retry();
        });
};

const _handleResponseErrors = (response, url, external = false) => {
    if (!response) {
        return;
    }

    if (response.status && !response.ok) {
        _manageError('server', response, url, external);
    }

    return response;
};

const _handleParseErrors = (error, url, isGet = false, silentOffline = false) => {
    if (error.message === 'TokenError' || error.name === 'AbortError' || error.name === 'SecurityError' || error.name === 'ChunkLoadError' || error.name === 'TimeoutError') {
        return {
            abort: true,
            abortReason: error.message === 'TokenError' ? 'TokenError' : error.name
        };
    }

    if (error.name === 'TypeError' &&
        (error.message === 'Failed to fetch' || error.message === 'NetworkError when attempting to fetch resource.')) {
        if (isGet && !silentOffline) {
            Notification.error(I18n.t('js.helpers.errors.no_network'));
        }

        return {
            errors: 'offline'
        };
    }

    _manageError('communication', error, url);

    return {
        errors: error.message
    };
};

const _handleResponse = (response) => {
    if (!response || !response.status) {
        return response;
    }

    if (response.bodyUsed) {
        return {
            errors: response.statusText || response.message,
            status: response.status
        };
    } else if (response.status === 422) { // Response must have a primary "errors" key to be processed
        return response.json();
    } else if (!response.ok) {
        // `status` rides alongside `errors` so a caller can tell a 5xx (the service is down) from a
        // 4xx (the request itself was refused — for the routing engine, "no road here"). The error
        // TEXT alone cannot: both arrive as an opaque string.
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.indexOf('application/json') !== -1) {
            return response.json()
                .then((status) => ({
                    errors: status.error || status.errors || response.statusText || status.message,
                    status: response.status
                }));
        } else {
            return response.text()
                .then((text) => ({
                    errors: text,
                    status: response.status
                }));
        }
    } else if (response.status !== 204) { // No content response
        return response.json();
    }

    return response;
};


const api = {
    get: (url, params = {}, requestParams = {}) => {
        params.locale ||= window.locale;

        const external = requestParams.external || false;
        const priorityLow = requestParams.priorityLow || false;
        const noCache = requestParams.noCache || false;
        // A background read whose caller handles its own failure: no "no network" toast.
        const silentOffline = requestParams.silentOffline || false;

        const headers = _getHeaders(external);
        const parameters = paramsToURL(params);
        let urlParams;
        if (external) {
            urlParams = url;
        } else {
            urlParams = `${url}.json${params ? '?' + parameters : ''}`;
        }

        let controller;
        let signal;
        if (typeof AbortController !== 'undefined') {
            if (requestParams.abort) {
                controller = new AbortController();
                signal = controller.signal;

                requestParams.abort.signal = controller;
            } else if (requestParams.timeout && typeof AbortSignal.timeout === 'function') {
                signal = AbortSignal.timeout(requestParams.timeout);
            }
        }

        if (priorityLow) {
            headers.priority = 'low';
        }
        if (noCache) {
            headers.cache = 'no-store';
        }

        return fetch(urlParams, {
            ...headers,
            method: 'GET',
            signal
        })
            .then((response) => _handleResponseErrors(response, urlParams, external))
            .then((response) => _handleResponse(response))
            .then((response) => handleJsonResponse(response))
            .catch((error) => _handleParseErrors(error, urlParams, true, silentOffline));
    },

    post: (url, params = {}, isData = false, requestParams = {}) => {
        params.locale ||= window.locale;

        const parameters = isData ? params : JSON.stringify(params);

        let controller;
        let signal;
        if (typeof AbortController !== 'undefined') {
            if (requestParams.abort) {
                controller = new AbortController();
                signal = controller.signal;

                requestParams.abort.signal = controller;
            } else if (requestParams.timeout && typeof AbortSignal.timeout === 'function') {
                signal = AbortSignal.timeout(requestParams.timeout);
            }
        }

        // Headers are rebuilt per attempt so a token-error replay picks up the refreshed CSRF token.
        const send = () => fetch(url + '.json', {
            ...(isData ? _getDataHeaders() : _getHeaders()),
            method: 'POST',
            body: parameters,
            signal
        });

        return send()
            .then((response) => _handleTokenError(response, send))
            .then((response) => _handleResponseErrors(response, url))
            .then((response) => _handleResponse(response))
            .then((response) => handleJsonResponse(response))
            .catch((error) => _handleParseErrors(error, url));
    },

    update: (url, params = {}, isData = false) => {
        params.locale ||= window.locale;

        const parameters = isData ? params : JSON.stringify(params);

        const send = () => fetch(url + '.json', {
            ...(isData ? _getDataHeaders() : _getHeaders()),
            method: 'PUT',
            body: parameters
        });

        return send()
            .then((response) => _handleTokenError(response, send))
            .then((response) => _handleResponseErrors(response, url))
            .then((response) => _handleResponse(response))
            .then((response) => handleJsonResponse(response))
            .catch((error) => _handleParseErrors(error, url));
    },

    delete: (url, params = {}) => {
        params.locale ||= window.locale;

        const headers = _getHeaders();
        const parameters = JSON.stringify(params);

        return fetch(url + '.json', {
            ...headers,
            method: 'DELETE',
            body: parameters
        })
            .then((response) => _handleResponseErrors(response, url))
            .then((response) => _handleResponse(response))
            .then((response) => handleJsonResponse(response))
            .catch((error) => _handleParseErrors(error, url));
    },

    sendBeacon: (url, params) => {
        return window.navigator.sendBeacon(url, _serializeParams(params));
    }
};

export const isBeaconSupported = () => typeof window.navigator.sendBeacon === 'function';

export default api;
